import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, LinearProgress
} from "@material-ui/core";
import TranscriptsService from "../../../../../services/api/transcripts.service";

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - patientId: string
 *  - onJobStarted?: (jobName: string) => void
 */
export default function UploadAudioDialog({ open, onClose, patientId, onJobStarted }) {
  const [file, setFile] = useState(null);           // File
  const [url, setUrl] = useState(null);             // object URL for preview
  const [duration, setDuration] = useState(null);   // seconds
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) {
      if (url) URL.revokeObjectURL(url);
      setUrl(null);
      setFile(null);
      setDuration(null);
      setUploading(false);
    }
  }, [open]); // eslint-disable-line

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // NOTE: Your backend signs with Content-Type=audio/mpeg today.
    // To avoid signature mismatch, restrict to MP3 for now.
    if (f.type && f.type !== "audio/mpeg" && f.type !== "audio/mp3") {
      alert("Please select an MP3 file.");
      return;
    }
    if (url) URL.revokeObjectURL(url);
    setFile(f);
    setUrl(URL.createObjectURL(f));
    setDuration(null);
  };

  const clearFile = () => {
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
    setFile(null);
    setDuration(null);
  };

  const uploadAndStart = async () => {
    if (!file) return;
    setUploading(true);
    try {
      // 1) presigned URL
      const { uploadURL, jobName } = await TranscriptsService.getUploadUrl(patientId);

      // 2) upload (content type MUST match what was signed)
      const contentType = file.type || "audio/mpeg";
      await TranscriptsService.uploadAudioToS3(uploadURL, file, contentType);

      // 3) start job
      await TranscriptsService.startMedicalScribeJob(patientId, jobName);

      onJobStarted && onJobStarted(jobName);
      alert("Upload complete. Transcription job started.");
      onClose();
    } catch (e) {
      console.error(e);
      alert("Upload or job start failed. Check S3 CORS/headers & API reachability.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Existing Audio</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <input
            type="file"
            accept="audio/mpeg,.mp3"
            onChange={onPick}
            disabled={uploading}
          />
          <Typography variant="caption" color="textSecondary" display="block">
            Accepted: MP3 files only.
          </Typography>
        </Box>

        {file && (
          <Box
            style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: 12 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
              {duration != null ? ` · ${Math.round(duration)}s` : ""}
            </Typography>
            <audio
              src={url}
              controls
              style={{ width: "100%", marginTop: 8 }}
              onLoadedMetadata={(e) => {
                try { setDuration(e.currentTarget.duration); } catch {}
              }}
            />
            <Box mt={2} display="flex" gridGap={8}>
              <Button
                variant="contained"
                color="primary"
                onClick={uploadAndStart}
                disabled={uploading}
              >
                {uploading ? "Uploading…" : "Upload & Start Transcript"}
              </Button>
              <Button variant="text" onClick={clearFile} disabled={uploading}>
                Clear
              </Button>
            </Box>
          </Box>
        )}

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
          </Box>
        )}

        {!file && (
          <Box mt={2} color="#666">
            <Typography variant="subtitle2">Tips</Typography>
            <ul style={{ marginTop: 4 }}>
              <li>Select an MP3 recording of the visit.</li>
            </ul>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <button className='btn btn-primary me-2 ' 
            onClick={onClose} disabled={uploading}>
            Close
        </button>
      </DialogActions>
    </Dialog>
  );
}
